import { useQuery } from '@apollo/client'
import { Icon, usePrompt, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Select, Table, UserSelect } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'
import { format } from 'date-fns'
import type {
  AllUserEquipmentQuery,
  AllUserEquipmentQueryVariables,
  AllUserEquipmentTypesQuery,
  ShallowUserNode,
} from '@/modules/users'
import {
  ALL_USER_EQUIPMENT_QUERY,
  ALL_USER_EQUIPMENT_TYPES_QUERY,
} from '@/modules/users/queries'
import React, { useEffect, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import type {
  EditUserEquipmentPromptResolve,
  UserEquipmentForSettings,
  UserEquipmentTypeForm,
  UserEquipmentTypeForSettings,
  UserEquipmentTypesSettingsForm,
} from '../components'
import {
  OldAddButton,
  EditUserEquipmentPrompt,
  InfoBox,
  OldSection,
  SectionHeader,
  SettingsWrapper,
  UserEquipmentTypePrompt,
  UserEquipmentTypeSettingsCard,
  UserEquipmentTypeSettingsTable,
} from '../components'

import {
  useUserEquipmentMutations,
  useUserEquipmentTypeMutations,
} from '../../../users/UserEquipmentTable'

const Header = styled.header`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 1rem;

  h2 {
    margin: 0 1rem 0 0;
  }

  ${props => props.theme.media.mobile} {
    display: grid;
    grid-template-rows: auto auto;
    gap: 0.5rem;

    margin-bottom: 0;
  }
`

const SelectFilter = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`

const TableRow = styled.tr`
  background: none;
`

interface UserEquipmentSettingsProps {}

export const UserEquipmentSettings: React.FC<
  UserEquipmentSettingsProps
> = () => {
  const translations = useTranslate({
    info1: 'settings.info.user-equipment-1',
    info2: 'settings.info.user-equipment-2',

    selectEquipmentType: 'settings.select-equipment-type',

    equipment: 'common.equipment',
    noEquipment: 'equipment.no-equipment',

    name: 'common.name',
    equipmentType: 'equipment.equipment-type',
    assignedDate: 'equipment.assigned-date',
    returnDate: 'equipment.return-date',
    user: 'common.user',

    prompt: {
      delete: ['settings.prompts.user-equipment-delete', { name: '' }],
      deleteTitle: 'settings.prompts.user-equipment-delete-title',
    },
    validation: {
      nameExists: 'settings.validation.user-equipment-type-name-exists',
    },
    userEquipmentTypes: 'common.user-equipment-types',
  })

  const confirm = useConfirm()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const mutations = useUserEquipmentMutations()
  const typeMutations = useUserEquipmentTypeMutations()

  const [user, setUser] = useState<ShallowUserNode | null>(null)
  const [equipmentType, setEquipmentType] =
    useState<UserEquipmentTypeForSettings | null>(null)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)

  const addToast = useToast()
  const equipmentTypeFilter = equipmentType ? [equipmentType.id] : null

  const { data: queryData, loading: queryLoading } = useQuery<
    AllUserEquipmentQuery,
    AllUserEquipmentQueryVariables
  >(ALL_USER_EQUIPMENT_QUERY, {
    variables: {
      user: user?.id,
      orderBy: 'assignedDate',
      equipmentType: equipmentTypeFilter?.[0],
    },
    onError: onErrorAuto(),
  })

  const { data: typeData } = useQuery<AllUserEquipmentTypesQuery>(
    ALL_USER_EQUIPMENT_TYPES_QUERY,
    {
      onError: onErrorAuto(),
    }
  )

  const editableTypes =
    typeData?.allUserEquipmentTypes.edges.map(edge => edge.node) ?? []

  const {
    formValues: form,
    formChangeHandler: handler,
    updateForm,
  } = useForm<UserEquipmentTypesSettingsForm>({
    values: {
      newName: '',
      newDescription: '',
      editName: '',
      editDescription: '',
    },
  })

  const equipment =
    queryData?.allUserEquipment.edges.map(edge => edge.node) ?? []

  async function handleEdit(equipment: UserEquipmentForSettings) {
    const { data } = await addPrompt<EditUserEquipmentPromptResolve | null>(
      resolve => (
        <EditUserEquipmentPrompt
          userEquipment={equipment}
          equipmentTypes={editableTypes}
          onSubmit={resolve}
        />
      )
    )
    if (data === null) return
    const { form } = data

    void mutations.patch({
      variables: {
        id: equipment.id,
        input: {
          name: form.name,
          equipmentType: form.equipmentType,
          assignedDate: form.assignedDate,
          returnDate: form.useReturnDate ? form.returnDate : null,
        },
      },
    })
  }

  async function handleDelete(equipment: UserEquipmentForSettings) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: equipment.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void mutations.delete({
      variables: { id: equipment.id },
    })
  }

  const isLoading = queryLoading || mutations.loading

  async function submitNewUserEquipmentType() {
    if (!validateName(form.newName)) return

    await typeMutations.create({
      variables: {
        input: {
          name: form.newName,
          description: form.newDescription,
        },
      },
    })
    setAdding(false)
  }

  async function handleCreatePrompt() {
    const { data } = await addPrompt<UserEquipmentTypeForm | null>(resolve => (
      <UserEquipmentTypePrompt
        editableTypes={editableTypes}
        onSubmit={resolve}
      />
    ))

    if (data === null) return

    void typeMutations.create({
      variables: {
        input: { ...data },
      },
    })
  }

  async function handleEditPrompt(type: UserEquipmentTypeForSettings) {
    const initialData: UserEquipmentTypeForm = {
      name: type.name,
      description: type.description,
    }
    const { data } = await addPrompt<UserEquipmentTypeForm | null>(resolve => (
      <UserEquipmentTypePrompt
        initialData={initialData}
        editableTypes={editableTypes}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void typeMutations.patch({
      variables: {
        id: type.id,
        input: { ...data },
      },
    })
  }

  function validateName(name: string) {
    if (name === '') return false
    if (editableTypes.find(type => type.name === form.newName)) {
      addToast('warning', translations.validation.nameExists)
      return false
    }
    return true
  }

  function checkNameHasChanged(id: string) {
    const type = editableTypes.find(type => type.id === id)
    if (typeof type === 'undefined') return true

    return (
      type.name !== form.editName || type.description !== form.editDescription
    )
  }

  async function submitEditUserEquipmentTypeName() {
    if (!validateName(form.editName) || editing === null) return
    if (!checkNameHasChanged(editing)) {
      setEditing(null)
      return
    }

    await typeMutations.patch({
      variables: {
        id: editing,
        input: {
          name: form.editName,
          description: form.editDescription,
        },
      },
    })
    setEditing(null)
  }

  async function handleDeletePrompt(type: UserEquipmentTypeForSettings) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: type.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void typeMutations.delete({
      variables: {
        id: type.id,
      },
    })
  }

  useEffect(() => {
    if (!adding && form.newName !== '') {
      updateForm({
        newName: '',
        newDescription: '',
      })
    }
  }, [adding, form.newName, updateForm])

  useEffect(() => {
    if (editing === null && form.editName !== '') {
      updateForm({ editName: '', editDescription: '' })
    }
  }, [editing, form.editName, updateForm])

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>
        <p>{translations.info1}</p>
        <p>{translations.info2}</p>
      </InfoBox>

      <OldSection>
        <Header>
          <SectionHeader loading={queryLoading}>
            {translations.userEquipmentTypes}
          </SectionHeader>
          {!isMobileOnly && (
            <OldAddButton adding={adding} onClick={handleCreatePrompt} />
          )}
        </Header>
        {isMobileOnly ? (
          <UserEquipmentTypeSettingsCard
            editableTypes={editableTypes}
            form={form}
            adding={adding}
            editing={editing}
            onFormChange={handler}
            onUpdateForm={updateForm}
            onAdd={setAdding}
            onAddSubmit={submitNewUserEquipmentType}
            onEdit={setEditing}
            onEditSubmit={submitEditUserEquipmentTypeName}
            onDelete={handleDeletePrompt}
          />
        ) : (
          <UserEquipmentTypeSettingsTable
            editableTypes={editableTypes}
            onEditClick={handleEditPrompt}
            onDelete={handleDeletePrompt}
          />
        )}
      </OldSection>

      <OldSection>
        <Header>
          <SectionHeader loading={isLoading}>
            {translations.equipment}
            {!isMobileOnly ? ':' : ''}
          </SectionHeader>
          <SelectFilter>
            <Select
              value={equipmentType?.id ?? null}
              fullWidth={isMobileOnly}
              onChange={(_, type) => setEquipmentType(type?.extra ?? null)}
              options={editableTypes.map(type => ({
                value: type.id,
                label: type.name,
                extra: type,
              }))}
              placeholder={translations.selectEquipmentType}
              searchable
              nullable
            />

            <UserSelect
              value={user?.id ?? null}
              fullWidth={isMobileOnly}
              onChange={(_, user) => setUser(user?.extra ?? null)}
              nullable
            />
          </SelectFilter>
        </Header>

        <Table
          noData={!isLoading && equipment.length === 0}
          noDataText={translations.noEquipment}
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.name}</th>
              <th>{translations.user}</th>

              {!isMobileOnly && (
                <>
                  <th>{translations.equipmentType}</th>
                  <th>{translations.assignedDate}</th>
                  <th>{translations.returnDate}</th>
                </>
              )}

              <th></th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {equipment.map(equipment => {
              const assignedDate = equipment.assignedDate
                ? format(new Date(equipment.assignedDate), 'PPP')
                : '-'
              const returnDate = equipment.returnDate
                ? format(new Date(equipment.returnDate), 'PPP')
                : '-'

              return (
                <TableRow key={equipment.id}>
                  <td>{equipment.name}</td>

                  {!isMobileOnly && (
                    <>
                      <td>
                        {!equipment.user ? (
                          '-'
                        ) : (
                          <PlainLink
                            to={`/users/${equipment.user.id}`}
                            $hoverUnderline
                            $hoverColor="secondary"
                          >
                            {equipment.user.fullName}
                          </PlainLink>
                        )}
                      </td>
                      <td>{equipment.equipmentType?.name || '-'}</td>
                      <td>{assignedDate}</td>
                      <td>{returnDate}</td>
                    </>
                  )}

                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(equipment)}
                    />
                  </td>

                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDelete(equipment)}
                    />
                  </td>
                </TableRow>
              )
            })}
          </tbody>
        </Table>
      </OldSection>
    </SettingsWrapper>
  )
}
